import type { Key } from 'react';
import type { PrimitiveAtom, WritableAtom } from 'jotai';
import { atom } from 'jotai';
export type VisibilityMode = {
  type: 'default';
} | {
  type: 'selected-only';
} | {
  type: 'show-all';
} | {
  type: 'label-only';
  label: string;
};
export const VISIBILITY_MODE = ({
  DEFAULT: 'default',
  SELECTED_ONLY: 'selected-only',
  SHOW_ALL: 'show-all',
  LABEL_ONLY: 'label-only'
} as const);
export type SidePanelAtoms = {
  expandedAnnotationIdsAtom: PrimitiveAtom<Set<Key>>;
  expandedAnnotationSectionIdsAtom: PrimitiveAtom<Set<Key>>;
  visibilityModeAtom: PrimitiveAtom<VisibilityMode>;
};
export type SidePanelDispatchAction = {
  type: 'SELECT_ANNOTATION';
  annotationId: string;
  sectionKey?: string;
} | {
  type: 'TOGGLE_SHOW_ALL';
} | {
  type: 'TOGGLE_LABEL_ONLY';
  label: string;
} | {
  type: 'RESET_VISIBILITY';
};
export type SidePanelAtomDispatch = WritableAtom<undefined, [action: SidePanelDispatchAction], void>;
export const createSidePanelAtoms = (): SidePanelAtoms & {
  sidePanelAtomDispatch: SidePanelAtomDispatch;
} => {
  const expandedAnnotationIdsAtom = atom<Set<Key>>(new Set<Key>());
  const expandedAnnotationSectionIdsAtom = atom<Set<Key>>(new Set<Key>());
  const visibilityModeAtom = atom<VisibilityMode>({
    type: VISIBILITY_MODE.DEFAULT
  });
  const sidePanelAtomDispatch: SidePanelAtomDispatch = atom(undefined, (get, set, action: SidePanelDispatchAction) => {
    switch (action.type) {
      case 'SELECT_ANNOTATION':
        {
          set(expandedAnnotationIdsAtom, prev => {
            const next = new Set(prev);
            next.add(action.annotationId);
            return next;
          });
          if (action.sectionKey) {
            const {
              sectionKey
            } = action;
            set(expandedAnnotationSectionIdsAtom, prev => {
              const next = new Set(prev);
              next.delete(sectionKey);
              return next;
            });
          }
          set(visibilityModeAtom, {
            type: VISIBILITY_MODE.SELECTED_ONLY
          });
          break;
        }
      case 'TOGGLE_SHOW_ALL':
        {
          const currentMode = get(visibilityModeAtom);
          const nextType = currentMode.type === VISIBILITY_MODE.SHOW_ALL ? VISIBILITY_MODE.SELECTED_ONLY : VISIBILITY_MODE.SHOW_ALL;
          set(visibilityModeAtom, {
            type: nextType
          });
          break;
        }
      case 'TOGGLE_LABEL_ONLY':
        {
          const currentMode = get(visibilityModeAtom);
          const isSameLabelActive = currentMode.type === VISIBILITY_MODE.LABEL_ONLY && currentMode.label === action.label;
          set(visibilityModeAtom, isSameLabelActive ? {
            type: VISIBILITY_MODE.DEFAULT
          } : {
            type: VISIBILITY_MODE.LABEL_ONLY,
            label: action.label
          });
          break;
        }
      case 'RESET_VISIBILITY':
        {
          set(visibilityModeAtom, {
            type: VISIBILITY_MODE.DEFAULT
          });
          break;
        }
    }
  });
  return {
    expandedAnnotationIdsAtom,
    expandedAnnotationSectionIdsAtom,
    visibilityModeAtom,
    sidePanelAtomDispatch
  };
};